import { makeAutoObservable } from 'mobx';
import { PayAtPaymentService } from '../../services/api/payment/payment-pay-at';
import { PaymentService } from './interfaces/payment-service';
import { RequestRapydCheckout } from './interfaces/request-rapyd-checkout';
import { PaymentPollingStore } from './payment-polling.store';
import { GeneratePaymentRequest } from './payment-request';
import { MCTransactionTypes, PaymentAttribution } from './enums/mc-transaction-types';
import { environment } from '../../../../environments/environment';

export enum PaymentPlatform {
  PayAtExternal = 'PayAtExternal',
  PayAtRedirect = 'PayAtRedirect',
  RapydRedirect = 'RapydRedirect',
  RapydEmbedded = 'RapydEmbedded',
}

export enum PaymentStatus {
  None,
  Success,
  Failure,
  Pending,
  Timeout,
}

export class PaymentStore {
  countryName!: string;
  customerNumber!: string;
  currencyCode!: string;
  lang!: string;
  pollingStore!: PaymentPollingStore;
  paymentMethod!: any;
  isDiscovery!: boolean;

  get status() {
    return this.pollingStore?.status;
  }

  get baseUserConfig() {
    return {
      countryName: this.countryName,
      customerNumber: this.customerNumber,
      currencyCode: this.currencyCode,
      lang: this.lang,
    };
  }

  get platform() {
    return this.paymentPlatform;
  }

  private paymentService!: PaymentService<any | RequestRapydCheckout>;
  private paymentPlatform!: PaymentPlatform;

  constructor() {
    makeAutoObservable(this);
  }

  static HandlePaymentResponse(params: URLSearchParams, onSuccess: () => void, onFailed: () => void) {
    if (params.get('payRes')) {
      if (params.get('payRes') === 'success') {
        onSuccess();
      } else if (params.get('payRes') === 'failed') {
        onFailed();
      }
      return true;
    }
    return false;
  }

  initialise(
    countryName: string,
    customerNumber: string,
    currencyCode: string,
    lang: string,
    paymentPlatform: PaymentPlatform,
    isDiscovery?: boolean
  ) {
    this.changeCountryName(countryName);
    this.changeCustomerNumber(customerNumber);
    this.changeCurrencyCode(currencyCode);
    this.changeLang(lang);
    this.changePaymentPlatform(paymentPlatform);
    this.setPaymentService();
    this.pollingStore = new PaymentPollingStore(this.paymentService, countryName);
    this.setIsDiscovery(isDiscovery ?? false);
  }

  changePaymentService(PaymentService: PaymentService<any | RequestRapydCheckout>) {
    this.paymentService = PaymentService;
  }

  changePaymentPlatform(paymentPlatform: PaymentPlatform) {
    this.paymentPlatform = paymentPlatform;
  }

  changeCountryName(countryName: string) {
    this.countryName = countryName;
  }

  changeCustomerNumber(customerNumber: string) {
    this.customerNumber = customerNumber;
  }

  changeCurrencyCode(currencyCode: string) {
    this.currencyCode = currencyCode;
  }

  changeLang(lang: string) {
    this.lang = lang;
  }

  changePaymentMethod(method: any) {
    this.paymentMethod = method;
  }

  setIsDiscovery(isDiscovery: boolean) {
    this.isDiscovery = isDiscovery;
  }

  initiatePayment(request: any, tokenize?: boolean) {
    return tokenize ? this.paymentService.TokenLinks(request) : this.paymentService.MakePayment(request);
  }

  makePayment(requestObject: any, tokenize?: boolean) {
    const baseReq = new GeneratePaymentRequest(this.paymentPlatform, {
      ...requestObject,
      ...this.baseUserConfig,
      failureReturnURL: `${requestObject.location}/failure`,
      successReturnURL: `${requestObject.location}/success`,
      backUrl: `${requestObject.location}`,
      paymentMethod: this.paymentMethod,
      isDiscovery: this.isDiscovery,
    });
    return baseReq
      .buildRequest(tokenize)
      .then((request) => this.initiatePayment(request, tokenize))
      .then(
        (result) => {
          return baseReq.request.handleResponse(result, requestObject.paymentAttribution).then((res) => {
            if (res) {
              return res;
            }
            return;
          });
        },
        (error) => {
          console.error('Failed to initialise payment; ', error);
          if (!error?.length) {
            return Promise.reject(error);
          }
          // TODO: Move retry logic into it's own polling service
          const [response, request] = error;
          if (response.status === 408 && this.paymentService.canRefetchPoll) {
            return this.paymentService.RefetchPayment(request?.orderId).then(
              (result) => {
                return baseReq.request.handleResponse(result, requestObject.paymentAttribution).then((res) => {
                  if (res) {
                    return res;
                  }
                  return;
                });
              },
              (error) => {
                console.error('Failed to refetch payment; ', error);
                return Promise.reject(error);
              }
            );
          }
          return Promise.reject(error);
        }
      );
  }

  payQuote(quote: any, location: string, paymentAttribution?: PaymentAttribution) {
    return this.makePayment({
      accountNumber: `${quote.financialAccountNumber}`,
      accountBalance: `${quote.accountBalance}`,
      paymentType: MCTransactionTypes.DstvPayment,
      amount: `${quote.payableAmount}`,
      amountToPay: `${quote.payableAmount}`,
      location: location,
      dueDate: quote.paymentDueDate as string,
      paymentAttribution,
    });
  }

  payChangePackage(
    fromPackage: any,
    toPackage: any,
    quote: any,
    location: string,
    paymentAttribution?: PaymentAttribution
  ) {
    return this.makePayment({
      smartCardNumber: fromPackage.smartcardNumber,
      paymentType: MCTransactionTypes.DstvPayment,
      amount: `${quote.payableAmount}`,
      productUserKey: `${fromPackage.name}`,
      productNamesPrices: `${toPackage.serviceName}:${toPackage.price}`,
      location: location,
      paymentAttribution,
    });
  }

  payAmount(account: any, amount: string, location: string, paymentAttribution?: PaymentAttribution) {
    return this.makePayment({
      accountNumber: `${account.accountNumber}`,
      accountBalance: `${account.accountBalance?.value}`,
      paymentType: MCTransactionTypes.DstvPayment,
      amount: amount,
      amountToPay: `${account.amountToPay?.value}`,
      location: location,
      dueDate: account.dueDate,
      paymentAttribution,
    });
  }

  payAndTokenize(quote: any, location: string, paymentAttribution?: PaymentAttribution) {
    return this.makePayment(
      {
        accountNumber: `${quote.financialAccountNumber}`,
        accountBalance: `${quote.accountBalance}`,
        paymentType: MCTransactionTypes.DstvPayment,
        amount: `${quote.payableAmount}`,
        amountToPay: `${quote.payableAmount}`,
        location: location,
        dueDate: quote.paymentDueDate as string,
        paymentAttribution,
      },
      true
    );
  }

  startPoll(transactionId: string, type: string) {
    this.pollingStore.startPoll(transactionId, type);
  }

  paymentFailed(reason: string) {
    this.pollingStore.paymentFailed(reason);
  }

  clearState() {
    this.pollingStore.clearState();
  }

  private setPaymentService() {
    switch (this.paymentPlatform) {
      case PaymentPlatform.PayAtRedirect:
        this.changePaymentService(new PayAtPaymentService());
        break;
    }
  }
}
